import React from "react";
import Joi from "joi-browser";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { StyledTableCell, StyledTableRow } from "../services/tableStyles";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { Pagination, Form, Modal, Button, Spinner } from "react-bootstrap";
import { RiDeleteBin2Line } from "react-icons/ri";
import { getCustomers, getUsersCount, deleteUser, addUser } from "../services/userService";
import PageLoading from "./common/pageLoading";
import FormComponent from "./common/formComponent";
import FloatingAddButton from "./common/floatingAddButton";

class Customers extends FormComponent {
  state = {
    data: {
      fullName: "",
      email: "",
      phone: "",
      category: "",
      permission: "None",
      address: "",
      notes: undefined,
    },
    loading: "",
    processing: false,
    errors: {},
    searchQuery: "",
    customers: [],
    customerToDelete: {},
    pageSize: 20,
    currentPage: 1,
    totalCustomers: 0,
    showAddModal: false,
    showConfirmModal: false,
  };

  constructor() {
    super();
    this.state.loading = true;
  }

  schema = {
    fullName: Joi.string().required().label("Full Name"),
    email: Joi.string().email().required().label("Email"),
    phone: Joi.string().required().label("Phone Number"),
    category: Joi.string().required().label("Category"),
    permission: Joi.string().required().label("Permission"),
    address: Joi.string().required().label("Address"),
    notes: Joi.string().max(50).optional().label("Notes"),
  };

  doSubmit = async () => {
    this.setState({ processing: true });
    const { data: payload, customers: originalCustomers } = this.state;

    try {
      const { data: newCustomer } = await addUser(payload);

      const customers = [...originalCustomers];
      customers.unshift(newCustomer);
      this.setState({ customers, showAddModal: false });
      toast.success("Customer Added");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ processing: false });
  };

  handleSearch = async (e) => {
    this.setState({ loading: true });
    const { currentPage, pageSize } = this.state;
    const value = e.currentTarget.value;
    this.setState({ searchQuery: value, currentPage: 1 });
    await this.populateCustomers(currentPage, pageSize, value);
  };

  handleDeleteCustomer = async (customerId) => {
    this.setState({ processing: true });
    const { customers: originalCustomers } = this.state;

    const customers = originalCustomers.filter((c) => c._id !== customerId);
    this.setState({ customers, showConfirmModal: false });

    try {
      await deleteUser(customerId);
      toast.success("Customer Deleted");
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
        this.setState({ customers: originalCustomers });
      }
    }
    this.setState({ processing: false });
  };

  showConfirmModal = (customerToDelete) => {
    this.setState({ showConfirmModal: true, customerToDelete });
  };

  handleShowAddModal = () => {
    this.setState({ showAddModal: true });
  };

  handleClose = () => {
    this.setState({ showAddModal: false, showConfirmModal: false });
  };

  async componentDidMount() {
    const { currentPage, pageSize, searchQuery } = this.state;
    await this.populateCustomers(currentPage, pageSize, searchQuery);
  }

  async populateCustomers(currentPage, pageSize, searchQuery) {
    try {
      const { data: customers } = await getCustomers(currentPage, pageSize, searchQuery); //limited result
      const { data: totalCustomers } = await getUsersCount("Customer", searchQuery);
      this.setState({ customers, totalCustomers });
    } catch (ex) {
      if (ex.response && ex.response.status >= 400 && ex.response.status < 500) {
        toast.error(ex.response.data);
      }
    }
    this.setState({ loading: false });
  }

  handlePageChange = async (direction) => {
    this.setState({ loading: true });
    const { currentPage, pageSize, searchQuery } = this.state;
    const newPage = direction === "next" ? currentPage + 1 : currentPage - 1;

    await this.populateCustomers(newPage, pageSize, searchQuery);
    this.setState({ currentPage: newPage });
  };

  render() {
    const {
      data,
      errors,
      customers,
      customerToDelete,
      pageSize,
      currentPage,
      totalCustomers,
      searchQuery,
      showAddModal,
      showConfirmModal,
      loading,
      processing,
    } = this.state;

    const pagesCount = Math.ceil(totalCustomers / pageSize);

    return (
      <React.Fragment>
        <Form>
          <Form.Group controlId="searchQuery">
            <Form.Control
              type="text"
              name="searchQuery"
              value={searchQuery}
              autoFocus
              autoComplete="off"
              onChange={this.handleSearch}
              placeholder="Search Customers By Name, Email or Phone Number..."
            />
          </Form.Group>
        </Form>
        <Paper elevation={3}>
          <div className="card">
            <div className="card-header main-color-bg">
              <div>
                <h5>Customers</h5>
              </div>
            </div>
            <div className="card-body">
              <span className="card-text">
                {loading && <PageLoading loading={loading} />}
                {!loading && customers.length === 0 && <span>No Customers to Display</span>}
                {!loading && <FloatingAddButton classes="float-right mt-2 mb-3" onClick={this.handleShowAddModal} />}
                {!loading && customers.length > 0 && (
                  <TableContainer component={Paper}>
                    <Table aria-label="customized table">
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>Full Name</StyledTableCell>
                          <StyledTableCell>Email</StyledTableCell>
                          <StyledTableCell>Phone Number</StyledTableCell>
                          <StyledTableCell>Category</StyledTableCell>
                          <StyledTableCell></StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {customers.map((customer) => (
                          <StyledTableRow key={customer._id}>
                            <StyledTableCell>
                              <Link to={`/customers/${customer._id}`}>{customer.fullName}</Link>
                            </StyledTableCell>
                            <StyledTableCell>{customer.email}</StyledTableCell>
                            <StyledTableCell>{customer.phone}</StyledTableCell>
                            <StyledTableCell>{customer.category}</StyledTableCell>
                            <StyledTableCell>
                              <Button
                                onClick={() =>
                                  this.showConfirmModal({
                                    _id: customer._id,
                                    fullName: customer.fullName,
                                  })
                                }
                                variant="danger"
                              >
                                <RiDeleteBin2Line />
                              </Button>
                            </StyledTableCell>
                          </StyledTableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                {!loading && pagesCount > 0 && (
                  <Pagination className="mt-5">
                    {pagesCount > 1 && (
                      <React.Fragment>
                        {currentPage !== 1 && (
                          <Pagination.Item onClick={() => this.handlePageChange("prev")}>Previous</Pagination.Item>
                        )}
                        <Pagination.Item>
                          Page {currentPage} of {pagesCount}
                        </Pagination.Item>
                        {currentPage !== pagesCount && (
                          <Pagination.Item onClick={() => this.handlePageChange("next")}>Next</Pagination.Item>
                        )}
                      </React.Fragment>
                    )}
                  </Pagination>
                )}
              </span>
            </div>
          </div>
        </Paper>

        <Modal show={showAddModal} onHide={this.handleClose}>
          <Modal.Header closeButton>
            <Modal.Title>Add Customer</Modal.Title>
          </Modal.Header>
          <Form onSubmit={this.handleSubmit}>
            <Modal.Body>
              <Form.Group controlId="fullName">
                <Form.Label>Full Name</Form.Label>
                <Form.Control
                  name="fullName"
                  onChange={this.handleChange}
                  value={data.fullName}
                  size="sm"
                  type="text"
                  autoFocus
                />
                {errors.fullName && <span className="text-danger">{errors.fullName}</span>}
              </Form.Group>

              <Form.Group controlId="email">
                <Form.Label>Email address</Form.Label>
                <Form.Control name="email" onChange={this.handleChange} value={data.email} size="sm" type="email" />
                {errors.email && <span className="text-danger">{errors.email}</span>}
              </Form.Group>

              <Form.Group controlId="phone">
                <Form.Label>Phone</Form.Label>
                <Form.Control name="phone" onChange={this.handleChange} value={data.phone} size="sm" type="tel" />
                {errors.phone && <span className="text-danger">{errors.phone}</span>}
              </Form.Group>

              <Form.Group controlId="category">
                <Form.Label>Category</Form.Label>
                <Form.Control as="select" name="category" value={data.category} onChange={this.handleChange}>
                  <option></option>
                  <option>Individual</option>
                  <option>Business</option>
                </Form.Control>
                {errors.category && <span className="text-danger">{errors.category}</span>}
              </Form.Group>

              <Form.Group controlId="address">
                <Form.Label>Address</Form.Label>
                <Form.Control
                  as="textarea"
                  rows="3"
                  name="address"
                  onChange={this.handleChange}
                  value={data.address}
                  size="sm"
                  type="address"
                />
                {errors.address && <span className="text-danger">{errors.address}</span>}
              </Form.Group>
            </Modal.Body>

            <Modal.Footer>
              <Button onClick={this.handleClose} variant="secondary">
                Close
              </Button>
              <Button disabled={this.validate() || processing} type="submit" variant="primary">
                {processing && (
                  <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
                )}
                Add
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>

        <Modal show={showConfirmModal} size="md" centered onHide={this.handleClose}>
          <Modal.Header closeButton className="bg-danger text-white">
            <Modal.Title>Delete Customer</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete the customer: {customerToDelete.fullName}?
            <br />
            <small className="text-danger">All the customer's equipment and services will be deleted</small>
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={this.handleClose} variant="secondary">
              No
            </Button>
            <Button
              disabled={processing}
              onClick={() => this.handleDeleteCustomer(customerToDelete._id)}
              type="submit"
              variant="primary"
            >
              {processing && (
                <Spinner as="span" className="mr-2" animation="border" size="sm" role="status" aria-hidden="true" />
              )}
              Yes
            </Button>
          </Modal.Footer>
        </Modal>
      </React.Fragment>
    );
  }
}

export default Customers;
